import Link from 'next/link';
import { FaSlack, FaTwitter, FaYoutube } from 'react-icons/fa';
import { RiBookLine } from 'react-icons/ri';
import Image from 'next/image';

import Layout from '@/components/Layout/Vanilla';
import Heading from '@/components/renderers/Heading';
import Button from '@/components/Button';
import Card from '@/components/sections/community/card';
import Newsletter from '@/components/sections/community/newsletter';
import ShowcaseCard from '@/components/sections/community/showcase';
import VideoCard from '@/components/sections/community/video';
import Testimonial from '@/components/sections/community/testimonial';
import { useIsomorphicLayoutEffect } from '@/hooks/useIsomorphicLayoutEffect';
import { fetchPage, gql } from '@/lib/graphcms';
import { getYouTubeData } from '@/pages/api/get-playlist-videos';

const PlayListIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M22 18V20H2V18H22ZM2 3.5L10 8.5L2 13.5V3.5ZM22 11V13H12V11H22ZM22 4V6H12V4H22Z"
      fill="currentColor"
    />
  </svg>
);

const ContributeIcon = () => (
  <svg
    width="21"
    height="20"
    viewBox="0 0 21 20"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M17 0C17.552 0 18 0.448 18 1V4.757L16 6.757V2H2V18H16V15.242L18 13.242V19C18 19.552 17.552 20 17 20H1C0.448 20 0 19.552 0 19V1C0 0.448 0.448 0 1 0H17ZM18.778 6.808L20.192 8.222L12.414 16L10.998 15.998L11 14.586L18.778 6.808ZM10 10V12H5V10H10ZM13 6V8H5V6H13Z"
      fill="currentColor"
    />
  </svg>
);

export default function Community({ page, showcase, youtubeVideos }) {
  const { title, subTitle } = page;

  useIsomorphicLayoutEffect(() => {
    document.querySelector('body').classList.add('dark-page');

    return () => {
      document.querySelector('body').classList.remove('dark-page');
    };
  }, []);

  return (
    <>
      <Layout seo={page} removeSalesBreakpoint>
        <div className="relative">
          <div className="!absolute top-[-224px] right-0 left-0 z-[-1] flex justify-center">
            <Image
              preload="true"
              src="/images/community/map.png"
              alt="Globe map"
              width={1353}
              height={596}
              layout="fixed"
              objectFit="cover"
            />
          </div>

          <div className="mt-20 lg:mt-30 mb-30 lg:mb-56 text-center max-w-3xl mx-auto px-6 lg:px-0">
            <Heading className="mt-0">{title}</Heading>
            <p className="text-lg leading-7 mt-6">{subTitle}</p>
          </div>

          <div className="bg-bunting">
            <div className="max-w-container mx-auto relative px-6 lg:px-0">
              <div className="transform translate-y-[-8%] lg:-translate-y-1/4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <Card
                    title="Join Slack"
                    description="Join our Slack community and engage with others building with GraphCMS."
                    icon={FaSlack}
                    href="https://slack.graphcms.com"
                  />
                  <Card
                    title="API Docs"
                    description="Get up to speed with our API and start building your next ambitious project."
                    icon={RiBookLine}
                    href="https://graphcms.com/docs"
                  />
                  <Card
                    title="Contribute to GraphCMS"
                    description="Collaborate with us to create content and get incentivized for teaching us new things."
                    icon={ContributeIcon}
                    href="https://graphcms.com/write-for-graphcms"
                  />
                  <Card
                    title="@ us on Twitter"
                    description="Follow us @GraphCMS and keep up to date with everything we’re up to."
                    icon={FaTwitter}
                    href="https://twitter.com/graphcms"
                  />
                  <Card
                    title="Watch Videos"
                    description="Explore our YouTube channel to see what we’re talking about on all things content."
                    icon={FaYoutube}
                    href="https://youtube.com/graphcms"
                  />
                  <Card
                    title="How to GraphCMS"
                    description="Schemas, fields, queries, mutations... Need a helping hand? We’ve got you covered."
                    icon={PlayListIcon}
                    href="https://www.youtube.com/playlist?list=PL5SvzogSTpeFSjhoWcXLDjuN2EYli78xd"
                  />
                </div>
              </div>

              <div className="pt-10 lg:pt-0 pb-30">
                <Newsletter />
              </div>
            </div>
          </div>

          <div className="max-w-container mx-auto px-6">
            <section>
              <div className="text-center mt-30 max-w-3xl mx-auto">
                <h2 className="font-bold text-3xl md:text-4xl lg:text-5xl leading-snug">
                  Community Driven Projects
                </h2>
                <p className="text-lg leading-7 mt-6">
                  We’re constantly astounded with the creativity and ambition
                  that our community shows with their projects. Here’s a
                  curation of all the goodies you’ve shared with us!
                </p>

                <Button
                  href="/showcase#submit-your-project"
                  colour="pink"
                  className="mt-10"
                >
                  Submit your project {`->`}
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-16 mt-20">
                {showcase.map(({ id, ...rest }) => (
                  <ShowcaseCard key={id} {...rest} />
                ))}
              </div>

              <div className="text-center mt-20">
                <Link href="/showcase" prefetch={false}>
                  <a className="group text-rose font-medium">
                    View Showcase
                    <span className="duration-100 group-hover:translate-x-1 inline-block ml-1 transform transition-transform">
                      &#8594;
                    </span>
                  </a>
                </Link>
              </div>
            </section>

            <section className="flex justify-between items-center flex-col md:flex-row mt-40">
              <div className="max-w-lg w-full space-y-6">
                <p className="text-sm uppercase tracking-wide font-medium text-rose">
                  Technical Writing Program
                </p>

                <h2 className="font-bold text-3xl md:text-4xl lg:text-5xl leading-snug">
                  Contribute to GraphCMS
                </h2>

                <p className="text-lg leading-7">
                  Want to create content on GraphCMS, GraphQL, or other
                  frameworks that could help developers learn? Get your content
                  published on GraphCMS!
                </p>

                <Button href="/write-for-graphcms" colour="pink">
                  Get involved {`->`}
                </Button>
              </div>

              <div className="mt-10 lg:mt-0">
                <Image
                  src="/images/community/community.svg"
                  width={528}
                  height={363}
                  alt="White box with GraphCMS and GraphQL logos, alongside with skeleton effect"
                />
              </div>
            </section>
          </div>

          <section className="bg-bunting mt-30">
            <div className="text-center py-30 max-w-3xl mx-auto px-6 lg:px-0">
              <h2 className="font-bold text-3xl md:text-4xl lg:text-5xl leading-snug">
                Hear from the Community
              </h2>
              <p className="text-lg leading-7 mt-6">
                Over 7,500 strong, we’re humbled to be playing a small role in
                the ambitious projects you’re all building.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-20 md:gap-7 max-w-container mx-auto px-6 pb-30">
              <Testimonial
                author={{
                  name: 'Michele Riva',
                  country: 'IT',
                  twitterHandle: 'MicheleRivaCode',
                  github: 'https://github.com/micheleriva',
                  photo: 'https://github.com/micheleriva.png?size=40',
                }}
                description="Not only are there people from the company itself who can help you understand how to optimize your GraphCMS experience, but there’s also a passionate and welcoming community of individual contributors doing their best to help each other. That’s something I genuinely care about when choosing the technologies for large-scale projects."
              />
              <Testimonial
                author={{
                  name: 'Irena Reitz',
                  country: 'DE',
                  github: 'https://github.com/irenama',
                  photo: 'https://github.com/irenama.png?size=40',
                }}
                description="The GraphCMS ecosphere is a great environment to explore - technically as well as in a broader sense. While discovering the CMS, you connect quickly in its very active community! Directly discuss questions, suggestions or simply exchange ideas!"
              />
              <Testimonial
                author={{
                  name: 'Colby Fayock',
                  country: 'US',
                  twitterHandle: 'colbyfayock',
                  github: 'https://github.com/colbyfayock',
                  photo: 'https://github.com/colbyfayock.png?size=40',
                }}
                description="Working with the GraphCMS team has been a joy. Beyond being kind and generally supportive, they've always been quick to jump in and give me (or others) a hand any time I don't completely understand how something works. The team clearly cares about the community and wants to do what they can to support it."
              />
            </div>
          </section>

          <section className="max-w-container mx-auto px-6">
            <div className="text-center mt-30 max-w-3xl mx-auto">
              <h2 className="font-bold text-3xl md:text-4xl lg:text-5xl leading-snug">
                Popular Videos
              </h2>
              <p className="text-lg leading-7 mt-6">
                Here are the videos we’ve created that were the most helpful to
                the community. Anything missing that you’d like us to cover, let
                us know!
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-10 gap-y-16 mt-20 max-w-[320px] sm:max-w-full mx-auto">
              {youtubeVideos.items.map(({ id, snippet, contentDetails }) => {
                const titleMatches = snippet.title.split(/\|/g);
                const videoTitle = titleMatches[0];

                return (
                  <VideoCard
                    key={id}
                    href={`https://www.youtube.com/watch?v=${contentDetails.videoId}`}
                    thumbnailUrl={snippet.thumbnails.medium.url}
                    title={videoTitle}
                  />
                );
              })}
            </div>
            <div className="text-center my-20">
              <a
                href="https://www.youtube.com/graphcms"
                target="_blank"
                rel="noopener noreferrer"
                className="group text-rose font-medium"
              >
                Watch more on YouTube
                <span className="duration-100 group-hover:translate-x-1 inline-block ml-1 transform transition-transform">
                  &#8594;
                </span>
              </a>
            </div>
          </section>
        </div>
      </Layout>
    </>
  );
}

export const getStaticProps = async ({ preview = false }) => {
  const { page, showcase } =
    (await fetchPage(
      gql`
        query communityPageQuery {
          page: page(where: { slug: "community" }) {
            ...pageData
          }
          showcase: showcases(where: { featured: true }, first: 6) {
            id
            authors {
              id
              name
              country
              photo {
                url(
                  transformation: {
                    image: { resize: { width: 40, height: 40, fit: clip } }
                  }
                )
                width
                height
                alt
              }
              gitHub
              twitterHandle
            }
            coverImage {
              url(
                transformation: {
                  image: { resize: { width: 372, height: 274, fit: clip } }
                }
              )
              width
              height
              alt
            }
            title
            externalUrl
            tags
            description
          }
        }
      `,
      {
        preview,
      },
    )) || [];

  const youtubeVideos = await getYouTubeData({
    playlistId: 'PL5SvzogSTpeFSjhoWcXLDjuN2EYli78xd',
    maxResults: 8,
  });

  return {
    props: {
      preview,
      page,
      showcase,
      youtubeVideos,
    },
    revalidate: 3600,
  };
};
